/**
 * The balance an account starts life with (spec 8.4, DBD-006): an entry against the equity
 * account, never a column on `accounts`. What the user declares at go-live is a fact about
 * the past, so it is recorded the same way as every other fact — a journal entry.
 *
 * Money held is debited to the account; money owed is credited to it, which is why a
 * liability's balance reads negative in `account_balances` and the UI says "Đang nợ …".
 */
export function buildOpeningEntry(input) {
    assertPositiveAmount('buildOpeningEntry', input.amount);
    const accountLine = input.ledgerKind === 'asset'
        ? { ledger_account_id: input.ledgerAccountId, debit_amount: input.amount }
        : { ledger_account_id: input.ledgerAccountId, credit_amount: input.amount };
    const equityLine = input.ledgerKind === 'asset'
        ? { ledger_account_id: input.openingBalancesLedgerAccountId, credit_amount: input.amount }
        : { ledger_account_id: input.openingBalancesLedgerAccountId, debit_amount: input.amount };
    return {
        economic_subject_id: input.subjectId,
        entry_type: 'opening',
        occurred_at: input.occurredAt,
        ...(input.description === undefined ? {} : { description: input.description }),
        lines: [accountLine, equityLine],
    };
}
/** Shared guard: money reaching the ledger is a positive, exactly representable amount. */
function assertPositiveAmount(builder, amount) {
    if (!Number.isSafeInteger(amount) || amount <= 0) {
        throw new Error(`${builder}: amount must be a positive integer VND amount`);
    }
}
/**
 * Shape 1 (spec 8.2): debit Expenses with the category / credit the account paid from.
 *
 * The credit side may be a credit card rather than cash or a bank account — swiping a card
 * IS the expense, and settling the statement later is a separate debt payment (G1/DBD-018).
 */
export function buildExpenseEntry(input) {
    assertPositiveAmount('buildExpenseEntry', input.amount);
    if (input.categoryType !== 'expense') {
        throw new Error('buildExpenseEntry: an expense needs a category of type expense');
    }
    return {
        // NEVER derived from who is logged in: the subject is what the entry belongs to (spec 6).
        economic_subject_id: input.forFamily === true ? input.householdSubjectId : input.selfSubjectId,
        entry_type: 'expense',
        occurred_at: input.occurredAt,
        ...(input.note === undefined || input.note === '' ? {} : { note: input.note }),
        lines: [
            {
                ledger_account_id: input.ledgerExpenseAccountId,
                debit_amount: input.amount,
                category_id: input.categoryId,
                ...(input.planningNature === undefined ? {} : { planning_nature: input.planningNature }),
            },
            { ledger_account_id: input.accountLedgerAccountId, credit_amount: input.amount },
        ],
    };
}
/** Shape 2 (spec 8.2): debit the account the money landed in / credit the income source. */
export function buildIncomeEntry(input) {
    assertPositiveAmount('buildIncomeEntry', input.amount);
    return {
        economic_subject_id: input.subjectId,
        entry_type: 'income',
        occurred_at: input.occurredAt,
        ...(input.note === undefined || input.note === '' ? {} : { note: input.note }),
        lines: [
            { ledger_account_id: input.destinationLedgerAccountId, debit_amount: input.amount },
            { ledger_account_id: input.incomeSourceLedgerAccountId, credit_amount: input.amount },
        ],
    };
}
/**
 * Shape 3 (spec 8.2): debit the destination / credit the source, both assets.
 *
 * Net worth does not change here, which is exactly why a transfer must not be recorded as an
 * expense on one side and income on the other — that is the double-count the ledger exists to
 * prevent (spec 11). Settling a credit card or repaying a loan is NOT this shape: it debits a
 * liability, which is `debt_payment` (step 1.3).
 */
export function buildTransferEntry(input) {
    assertPositiveAmount('buildTransferEntry', input.amount);
    if (input.fromLedgerAccountId === input.toLedgerAccountId) {
        throw new Error('buildTransferEntry: source and destination must be different accounts');
    }
    return {
        economic_subject_id: input.subjectId,
        entry_type: 'transfer',
        occurred_at: input.occurredAt,
        ...(input.note === undefined || input.note === '' ? {} : { note: input.note }),
        lines: [
            {
                ledger_account_id: input.toLedgerAccountId,
                debit_amount: input.amount,
                ...(input.purpose === undefined ? {} : { purpose: input.purpose }),
            },
            { ledger_account_id: input.fromLedgerAccountId, credit_amount: input.amount },
        ],
    };
}
/**
 * Shape 4 (spec 8.2): "lương về chia 5 ngả" as ONE entry — credit the source account once,
 * debit every destination.
 *
 * Splitting it into five separate transfers would be the double count the ledger exists to
 * prevent (spec 11): each leg would look like money arriving somewhere, and the salary that
 * funded them all would be counted again on top.
 *
 * The single credit IS the sum of the legs — the one place a total is computed rather than
 * mirrored. DBD-014 bans float arithmetic on money, not this: VND amounts are integers,
 * every leg is checked to be a safe integer and so is the total, so the sum is exact. The
 * database re-checks the balance at COMMIT regardless (`assert_entry_balanced`).
 */
export function buildSplitEntry(input) {
    // The shape needs at least two debit lines; with one leg this is a plain transfer or
    // expense, which say what they are far better than a split of one.
    if (input.allocations.length < 2) {
        throw new Error('buildSplitEntry: a split needs at least two allocations');
    }
    let total = 0;
    for (const allocation of input.allocations) {
        assertPositiveAmount('buildSplitEntry', allocation.amount);
        if (allocation.kind === 'transfer' &&
            allocation.toLedgerAccountId === input.sourceLedgerAccountId) {
            throw new Error('buildSplitEntry: an allocation cannot move money back to the source');
        }
        total += allocation.amount;
    }
    // Only reachable with amounts far beyond household money, but the credit line is a value
    // the ledger will store — it may not be an approximation.
    if (!Number.isSafeInteger(total)) {
        throw new Error('buildSplitEntry: the total of the allocations is not exactly representable');
    }
    const debitLines = input.allocations.map((allocation) => allocation.kind === 'transfer'
        ? {
            ledger_account_id: allocation.toLedgerAccountId,
            debit_amount: allocation.amount,
            ...(allocation.purpose === undefined ? {} : { purpose: allocation.purpose }),
        }
        : {
            ledger_account_id: input.ledgerExpenseAccountId,
            debit_amount: allocation.amount,
            category_id: allocation.categoryId,
            ...(allocation.planningNature === undefined
                ? {}
                : { planning_nature: allocation.planningNature }),
        });
    return {
        economic_subject_id: input.subjectId,
        entry_type: 'split',
        occurred_at: input.occurredAt,
        ...(input.note === undefined || input.note === '' ? {} : { note: input.note }),
        lines: [
            ...debitLines,
            { ledger_account_id: input.sourceLedgerAccountId, credit_amount: total },
        ],
    };
}
/**
 * Shape 5 (spec 8.2): debit the liability / credit the account paying it — the debt shrinks
 * and so does the cash, so net worth is unchanged.
 *
 * Two different meanings share this shape, and the cash-flow report tells them apart by the
 * account type, not by the entry (G1/DBD-018): repaying a real loan is DEBT_PRINCIPAL, while
 * settling a credit card statement is INTERNAL because the expense was already recorded when
 * the card was swiped. Counting it again here would report the same money twice.
 */
export function buildDebtPaymentEntry(input) {
    assertPositiveAmount('buildDebtPaymentEntry', input.amount);
    if (input.liabilityLedgerAccountId === input.fromLedgerAccountId) {
        throw new Error('buildDebtPaymentEntry: an account cannot pay off itself');
    }
    return {
        economic_subject_id: input.subjectId,
        entry_type: 'debt_payment',
        occurred_at: input.occurredAt,
        ...(input.note === undefined || input.note === '' ? {} : { note: input.note }),
        lines: [
            { ledger_account_id: input.liabilityLedgerAccountId, debit_amount: input.amount },
            { ledger_account_id: input.fromLedgerAccountId, credit_amount: input.amount },
        ],
    };
}
//# sourceMappingURL=entryBuilders.js.map